/**
 * World Space Utilities
 * 
 * Calculates absolute positions for all frames in a 2D world space.
 * This enables position-based navigation instead of index-based navigation.
 */

import type { OffsetMap } from '@/types/offsetMap'
import { extractOffsetsFromManifest, type Manifest } from './calculateOffsetMapFromManifest'

export interface FrameWorldPosition {
  frameIndex: number
  worldX: number
  worldY: number
}

/**
 * Calculate world space positions for all frames (with manifest fallback)
 * 
 * Uses manifest.json if available (faster, smaller data), otherwise falls back to offset_map.
 * Automatically detects which source has usable data.
 * 
 * @param offsetMap - Pre-calculated offset map (fallback if no manifest)
 * @param totalFrames - Total number of frames in dataset
 * @param manifest - Optional manifest with quality-filtered offsets (preferred if available)
 * @param frameWidth - Width of each frame (optional, for centering)
 * @param frameHeight - Height of each frame (optional, for centering)
 * @returns Array of world positions for each frame
 */
export function calculateFrameWorldPositions(
  offsetMap: OffsetMap,
  totalFrames: number,
  manifest?: Manifest,
  frameWidth?: number,
  frameHeight?: number
): FrameWorldPosition[] {
  // Prefer manifest if available (faster, smaller data)
  if (manifest && manifest.frames && manifest.frames.length > 0) {
    console.log('[WorldSpace] Using manifest.json for position calculation')
    return calculateFrameWorldPositionsFromManifest(manifest, frameWidth, frameHeight)
  }
  
  // Fall back to offset_map approach
  console.log('[WorldSpace] Using offset_map.json for position calculation')
  return calculateFrameWorldPositionsFromOffsetMap(offsetMap, totalFrames, frameWidth, frameHeight)
}

/**
 * Calculate world space positions from offset_map.json (legacy approach)
 * 
 * @param offsetMap - Pre-calculated offset map with sequential offsets
 * @param totalFrames - Total number of frames in dataset
 * @param frameWidth - Width of each frame (optional, for centering)
 * @param frameHeight - Height of each frame (optional, for centering)
 * @returns Array of world positions for each frame (centered at frame center, not top-left)
 */
export function calculateFrameWorldPositionsFromOffsetMap(
  offsetMap: OffsetMap,
  totalFrames: number,
  frameWidth?: number,
  frameHeight?: number
): FrameWorldPosition[] {
  const positions: FrameWorldPosition[] = []
  
  // Frame 0 centered at world origin (0, 0)
  // Start at top-left relative position: (-frameWidth/2, -frameHeight/2)
  // This way when we add half-dimensions, Frame 0's center ends up at (0, 0)
  const frame0X = frameWidth ? -frameWidth / 2 : 0
  const frame0Y = frameHeight ? -frameHeight / 2 : 0
  
  positions.push({
    frameIndex: 0,
    worldX: frame0X,
    worldY: frame0Y
  })
  
  // Calculate cumulative positions for each subsequent frame
  for (let i = 1; i < totalFrames; i++) {
    const prevPosition = positions[i - 1]
    
    // Get sequential offset from previous frame to this frame
    const offsetKey = `${i - 1}_${i}`
    const offset = offsetMap.all_pairs?.[offsetKey] || offsetMap.sequential?.[offsetKey]
    
    if (!offset) {
      positions.push({
        frameIndex: i,
        worldX: prevPosition.worldX,
        worldY: prevPosition.worldY
      })
      continue
    }
    
    // Add offset to previous position to get absolute world position
    positions.push({
      frameIndex: i,
      worldX: prevPosition.worldX + offset.dx,
      worldY: prevPosition.worldY + offset.dy
    })
  }
  
  // Apply center offset if frame dimensions provided
  // This shifts all positions so each frame's center is at its stored world position
  if (frameWidth && frameHeight) {
    const centerOffsetX = frameWidth / 2
    const centerOffsetY = frameHeight / 2
    positions.forEach(pos => {
      pos.worldX += centerOffsetX
      pos.worldY += centerOffsetY
    })
  }
  
  // Calculate world space bounds
  const minX = Math.min(...positions.map(p => p.worldX))
  const maxX = Math.max(...positions.map(p => p.worldX))
  const minY = Math.min(...positions.map(p => p.worldY))
  const maxY = Math.max(...positions.map(p => p.worldY))
  
  console.log(`[WorldSpace] Calculated ${totalFrames} positions: x=${(maxX - minX).toFixed(0)}px, y=${(maxY - minY).toFixed(0)}px`)
  
  return positions
}

/**
 * Calculate world space positions for all frames using manifest.json data
 * 
 * Uses BFS traversal over ALL available neighbor offsets (not just sequential).
 * This handles gaps where sequential offset (i-1)→i is missing by routing
 * through alternative paths (e.g., frame 6 reachable via 4→6 or 7→6).
 * 
 * Falls back to sequential-only positioning if useBFS=false.
 * 
 * @param manifest - Manifest containing quality-filtered neighbor offsets
 * @param frameWidth - Width of each frame (optional, for centering)
 * @param frameHeight - Height of each frame (optional, for centering)
 * @param useBFS - Use BFS positioning (default: true). Set false for legacy sequential behavior.
 * @returns Array of world positions for each frame (centered at frame center, not top-left)
 */
export function calculateFrameWorldPositionsFromManifest(
  manifest: Manifest,
  frameWidth?: number,
  frameHeight?: number,
  useBFS: boolean = true
): FrameWorldPosition[] {
  const totalFrames = manifest.frames.length
  
  console.log(`[calculateFrameWorldPositionsFromManifest] Starting: ${totalFrames} frames, dimensions=${frameWidth}x${frameHeight}, mode=${useBFS ? 'BFS' : 'sequential'}`)
  
  // Extract all_pairs from manifest (quality-filtered pairs only)
  const allPairs = extractOffsetsFromManifest(manifest)
  
  const frame0X = frameWidth ? -frameWidth / 2 : 0
  const frame0Y = frameHeight ? -frameHeight / 2 : 0
  
  let positions: FrameWorldPosition[]
  
  if (useBFS) {
    positions = calculatePositionsBFS(allPairs, totalFrames, frame0X, frame0Y)
  } else {
    positions = calculatePositionsSequential(allPairs, totalFrames, frame0X, frame0Y)
  }
  
  // Apply center offset if frame dimensions provided
  if (frameWidth && frameHeight) {
    const centerOffsetX = frameWidth / 2
    const centerOffsetY = frameHeight / 2
    positions.forEach(pos => {
      pos.worldX += centerOffsetX
      pos.worldY += centerOffsetY
    })
  }
  
  // Calculate world space bounds
  const minX = Math.min(...positions.map(p => p.worldX))
  const maxX = Math.max(...positions.map(p => p.worldX))
  const minY = Math.min(...positions.map(p => p.worldY))
  const maxY = Math.max(...positions.map(p => p.worldY))
  
  const spanX = (maxX - minX).toFixed(0)
  const spanY = (maxY - minY).toFixed(0)
  console.log(`[WorldSpace] ✅ Calculated ${totalFrames} positions from manifest (${useBFS ? 'BFS' : 'sequential'})`)
  console.log(`   📐 Bounds: x=${spanX}px, y=${spanY}px`)
  console.log(`   📍 First frame: (${positions[0].worldX.toFixed(0)}, ${positions[0].worldY.toFixed(0)})`)
  console.log(`   📍 Last frame: (${positions[totalFrames-1].worldX.toFixed(0)}, ${positions[totalFrames-1].worldY.toFixed(0)})`)
  
  return positions
}

/**
 * BFS-based positioning: traverse ALL available offsets starting from frame 0.
 * 
 * Advantages over sequential:
 * - Handles gaps where sequential offset (i-1)→i is missing
 * - Uses shortest path (fewest hops) which minimizes drift
 * - Prefers sequential neighbors (|from - to| = 1) at same BFS depth for best accuracy
 */
function calculatePositionsBFS(
  allPairs: Map<string, { dx: number; dy: number }>,
  totalFrames: number,
  frame0X: number,
  frame0Y: number
): FrameWorldPosition[] {
  // Build adjacency list from allPairs
  const adjacency = new Map<number, Array<{ neighbor: number; dx: number; dy: number }>>()
  
  for (const [key, offset] of allPairs) {
    const underscoreIdx = key.indexOf('_')
    const from = parseInt(key.substring(0, underscoreIdx), 10)
    const to = parseInt(key.substring(underscoreIdx + 1), 10)
    
    if (!adjacency.has(from)) adjacency.set(from, [])
    adjacency.get(from)!.push({ neighbor: to, dx: offset.dx, dy: offset.dy })
  }
  
  // Sort each adjacency list: prefer sequential neighbors first (smallest |from - to|)
  for (const [frameId, neighbors] of adjacency) {
    neighbors.sort((a, b) => Math.abs(a.neighbor - frameId) - Math.abs(b.neighbor - frameId))
  }
  
  // BFS from frame 0
  const positionMap = new Map<number, { worldX: number; worldY: number }>()
  positionMap.set(0, { worldX: frame0X, worldY: frame0Y })
  
  const queue: number[] = [0]
  const visited = new Set<number>([0])
  
  while (queue.length > 0) {
    const current = queue.shift()!
    const currentPos = positionMap.get(current)!
    const neighbors = adjacency.get(current) || []
    
    for (const { neighbor, dx, dy } of neighbors) {
      if (!visited.has(neighbor) && neighbor >= 0 && neighbor < totalFrames) {
        visited.add(neighbor)
        positionMap.set(neighbor, {
          worldX: currentPos.worldX + dx,
          worldY: currentPos.worldY + dy
        })
        queue.push(neighbor)
      }
    }
  }
  
  // Handle disconnected frames (not reachable by BFS)
  const disconnectedCount = totalFrames - visited.size
  if (disconnectedCount > 0) {
    console.warn(`[WorldSpace BFS] ⚠️ ${disconnectedCount} frames unreachable by BFS, using nearest neighbor fallback`)
  }
  
  for (let i = 0; i < totalFrames; i++) {
    if (!positionMap.has(i)) {
      // Try nearest visited neighbor
      const prev = positionMap.get(i - 1)
      const next = positionMap.get(i + 1)
      if (prev) {
        positionMap.set(i, { worldX: prev.worldX, worldY: prev.worldY })
      } else if (next) {
        positionMap.set(i, { worldX: next.worldX, worldY: next.worldY })
      } else {
        positionMap.set(i, { worldX: frame0X, worldY: frame0Y })
      }
    }
  }
  
  // Convert to sorted array
  const positions: FrameWorldPosition[] = []
  for (let i = 0; i < totalFrames; i++) {
    const pos = positionMap.get(i)!
    positions.push({ frameIndex: i, worldX: pos.worldX, worldY: pos.worldY })
  }
  
  console.log(`[WorldSpace BFS] Reached ${visited.size}/${totalFrames} frames by BFS, ${disconnectedCount} fallback`)
  
  return positions
}

/**
 * Legacy sequential positioning: cumulative offsets from frame 0 → 1 → 2 → ...
 * Kept for reversibility (useBFS=false).
 */
function calculatePositionsSequential(
  allPairs: Map<string, { dx: number; dy: number }>,
  totalFrames: number,
  frame0X: number,
  frame0Y: number
): FrameWorldPosition[] {
  const positions: FrameWorldPosition[] = []
  
  positions.push({ frameIndex: 0, worldX: frame0X, worldY: frame0Y })
  
  for (let i = 1; i < totalFrames; i++) {
    const prevPosition = positions[i - 1]
    const offsetKey = `${i - 1}_${i}`
    const offset = allPairs.get(offsetKey)
    
    if (!offset) {
      positions.push({ frameIndex: i, worldX: prevPosition.worldX, worldY: prevPosition.worldY })
      continue
    }
    
    positions.push({
      frameIndex: i,
      worldX: prevPosition.worldX + offset.dx,
      worldY: prevPosition.worldY + offset.dy
    })
  }
  
  return positions
}

/**
 * Get world bounds (min/max X and Y) for all frames
 */
export function getWorldBounds(positions: FrameWorldPosition[], frameWidth: number, frameHeight: number) {
  if (positions.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
  }
  
  const minX = Math.min(...positions.map(p => p.worldX))
  const maxX = Math.max(...positions.map(p => p.worldX)) + frameWidth
  const minY = Math.min(...positions.map(p => p.worldY))
  const maxY = Math.max(...positions.map(p => p.worldY)) + frameHeight
  
  return { minX, maxX, minY, maxY }
}

/**
 * Calculate anchored frame positions relative to a selected frame
 * 
 * Instead of cumulative offsets from frame 0, each frame is positioned
 * at its direct offset from the selected frame. This allows spatial
 * relationship visualization when changing the selected frame.
 * 
 * @param selectedFrameIndex - The frame to use as anchor (0,0)
 * @param offsetMap - Pre-calculated offset map with all frame pairs
 * @param totalFrames - Total number of frames
 * @param cumulativePositions - Optional: fallback positions if direct offset not found
 * @returns Array of anchored positions with selected frame at (0,0)
 */
export function calculateAnchoredFramePositions(
  selectedFrameIndex: number,
  offsetMap: OffsetMap,
  totalFrames: number,
  cumulativePositions?: FrameWorldPosition[]
): FrameWorldPosition[] {
  const positions: FrameWorldPosition[] = []
  
  // Selected frame is always at origin
  positions.push({
    frameIndex: selectedFrameIndex,
    worldX: 0,
    worldY: 0
  })
  
  // For each other frame, calculate direct offset from selected frame
  for (let i = 0; i < totalFrames; i++) {
    if (i === selectedFrameIndex) {
      continue // Already added
    }
    
    // Look up direct offset from selected frame to this frame
    const offsetKey = `${selectedFrameIndex}_${i}`
    const offset = offsetMap.all_pairs?.[offsetKey]
    
    if (offset) {
      // Direct offset found - use it
      positions.push({
        frameIndex: i,
        worldX: offset.dx,
        worldY: offset.dy
      })
    } else {
      // Fallback: calculate from cumulative positions if available
      if (cumulativePositions && cumulativePositions.length > Math.max(selectedFrameIndex, i)) {
        const selectedPos = cumulativePositions[selectedFrameIndex]
        const targetPos = cumulativePositions[i]
        
        positions.push({
          frameIndex: i,
          worldX: targetPos.worldX - selectedPos.worldX,
          worldY: targetPos.worldY - selectedPos.worldY
        })
      } else {
        // No offset found and no cumulative fallback - skip this frame
        console.warn(`⚠️  [calculateAnchoredPositions] No offset found for pair ${offsetKey} and no cumulative fallback`)
        positions.push({
          frameIndex: i,
          worldX: 0,
          worldY: 0
        })
      }
    }
  }
  
  // Sort by frame index for consistent ordering
  positions.sort((a, b) => a.frameIndex - b.frameIndex)
  
  console.log(`[AnchoredPositions] Calculated positions anchored to frame ${selectedFrameIndex}: ${positions.length} frames`)
  
  return positions
}
